import * as React from 'react';

import * as ramda from 'ramda';

import {Session} from 'nutshell-core/session';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Card} from 'shells/card/card';
import {CollapsibleCard} from 'shells/card/collapsible-card';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import type {SelectMenuOption} from 'shells/select-menu/select-menu';
import {Body} from 'shells/typography/body';

import type {UserQuotaFragment as User} from 'nutshell-graphql-types';

import {DuplicateToUsersModal} from './duplicate-to-users-modal';
import {
    buildInitialMonthValues,
    deriveGridData,
    getFiscalYearStartMonth,
    type MonthValues,
} from './helpers';
import {UserQuotaCardHeader} from './user-quota-card-header';
import {UserQuotaGrid} from './user-quota-grid';

import styles from './user-quota-card.module.css';

type Props = {
    user: User;
    existingUsers?: User[];
    fiscalYear: number;
    isEditing: boolean;
    shouldStartOpen?: boolean;
    onSave: (monthValues: MonthValues, isNewQuota: boolean) => void;
    onEdit?: () => void;
    onCancelEdit?: () => void;
    onEditChange?: (isDirty: boolean) => void;
    onDelete?: () => Promise<void>;
    onDuplicateStart?: (userIds: string[]) => void;
    onCardOpened?: (userId: string) => void;
    getRef?: (node: HTMLDivElement | null) => void;
    setScrollToUserId?: (userId: string) => void;
};

const cardShellProps = {
    hasBorder: true as const,
    hasShadow: false as const,
    overflowVisible: true as const,
    fitContent: false as const,
    fitHeight: true as const,
    borderRadius: 8,
};

export function UserQuotaCard(props: Props) {
    const {isEditing, onEditChange, shouldStartOpen, onCardOpened, user} = props;
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = React.useState(false);
    const [calendarMonthToFocus, setCalendarMonthToFocus] = React.useState<number | null>(null);
    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = React.useState<string | undefined>();

    const existingQuota = user.quotas.find((quota) => quota.fiscalYear === props.fiscalYear);
    const previousYearQuota = user.quotas.find(
        (quota) => quota.fiscalYear === props.fiscalYear - 1
    );
    const [draftByMonth, setDraftByMonth] = React.useState<MonthValues>(
        buildInitialMonthValues(existingQuota)
    );

    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    const cardRef = React.useRef<HTMLDivElement | null>(null);

    const {adjMonths, quarterValues, yearTotal} = React.useMemo(
        () => deriveGridData(getFiscalYearStartMonth(), draftByMonth),
        [draftByMonth]
    );

    const collapsibleHeaderActionMenuOptions: SelectMenuOption[] = [
        {
            label: 'Copy to another user',
            variant: 'secondary',
            handleSelect: () => setIsDuplicateModalOpen(true),
        },
        {
            label: 'Delete',
            variant: 'destructive',
            handleSelect: () => setIsConfirmingDelete(true),
        },
    ];

    const formattedYearTotal = yearTotal.toLocaleString(undefined, {
        style: 'currency',
        currency: Session.getInstanceDefaultCurrencyCode(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const shouldShowStatusBadge = !existingQuota && !props.isEditing;
    const header = (
        <UserQuotaCardHeader
            user={user}
            fiscalYear={props.fiscalYear}
            previousYearQuota={previousYearQuota}
            existingQuota={existingQuota}
            isEditing={props.isEditing}
            onEdit={props.onEdit ? handleHeaderEdit : undefined}
            onCopyFromPreviousYear={
                previousYearQuota && !existingQuota ? handleCopyFromPreviousYear : undefined
            }
            showStatusBadge={shouldShowStatusBadge}
            isOpen={isOpen}
            hasActionMenu={Boolean(existingQuota)}
            formattedYearTotal={formattedYearTotal}
        />
    );

    const grid = (
        <UserQuotaGrid
            adjMonths={adjMonths}
            quarterValues={quarterValues}
            fiscalYear={props.fiscalYear}
            isEditing={props.isEditing}
            calendarMonthToFocus={calendarMonthToFocus}
            setIsEditing={handleCellClick}
            onMonthValueChange={(month, amount) =>
                setDraftByMonth((draft) => ({
                    ...draft,
                    [month]: amount,
                }))
            }
        />
    );

    const totalRow = (
        <div className='flex justify-end align-center gap-32 pad-16 border-box'>
            <Body color='dark' size='small' isBold={true}>
                {props.fiscalYear} total
            </Body>
            <Body color='dark' isBold={true}>
                {formattedYearTotal}
            </Body>
        </div>
    );

    function handleCellClick(calendarMonthToFocus: number) {
        setCalendarMonthToFocus(calendarMonthToFocus);
        props.onEdit?.();
    }

    function handleHeaderEdit() {
        setCalendarMonthToFocus(null);
        setIsOpen(true);
        props.onEdit?.();
    }

    function handleCopyFromPreviousYear() {
        if (!previousYearQuota) {
            return;
        }
        setDraftByMonth(buildInitialMonthValues(previousYearQuota));
        setCalendarMonthToFocus(null);
        props.onEdit?.();
        setIsOpen(true);
    }

    const handleSave = async () => {
        return props.onSave(draftByMonth, !existingQuota);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !(e.target instanceof HTMLInputElement)) {
            e.preventDefault();
            handleSave();
        }
    };

    // Refocuses the card after a user finishes editing the input values.
    // Works in combination with handleKeyDown so users can quickly save changes with Enter.
    const handleBlurCapture = (e: React.FocusEvent) => {
        if (
            e.target instanceof HTMLInputElement &&
            !(e.relatedTarget instanceof HTMLInputElement)
        ) {
            cardRef.current?.focus();
        }
    };

    React.useEffect(() => {
        setDraftByMonth(buildInitialMonthValues(existingQuota));
    }, [existingQuota]);

    React.useEffect(() => {
        if (draftByMonth && isEditing) {
            // @ts-expect-error - don't track onChange to prevent infinite re-renders
            onEditChange(!ramda.equals(buildInitialMonthValues(existingQuota), draftByMonth));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftByMonth, existingQuota, isEditing]);

    React.useEffect(() => {
        if (shouldStartOpen && !isOpen) {
            setIsOpen(true);
            onCardOpened?.(user.id);
        }
    }, [shouldStartOpen, isOpen, onCardOpened, user.id]);

    if (props.isEditing) {
        return (
            <div
                ref={(node) => {
                    cardRef.current = node;
                    if (props.getRef) {
                        props.getRef(node);
                    }
                }}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                onBlurCapture={handleBlurCapture}
            >
                <Card {...cardShellProps}>
                    <div className='flex align-center justify-sb py-8 px-8 gap-16'>
                        {header}
                        <div className='flex gap-12 align-center text-nowrap'>
                            <Button
                                variant='text'
                                type='button'
                                onClick={() => {
                                    setDraftByMonth(buildInitialMonthValues(existingQuota));
                                    props.onCancelEdit?.();
                                }}
                            >
                                Cancel
                            </Button>
                            <SaveButton
                                buttonText='Save changes'
                                savingText='Saving…'
                                successText='Changes saved!'
                                isSaving={false}
                                onClick={handleSave}
                                size='normal'
                                resetOnComplete={false}
                            />
                        </div>
                    </div>
                    {grid}
                    {totalRow}
                </Card>
            </div>
        );
    }

    return (
        <>
            <CollapsibleCard
                header={header}
                headerActionMenuOptions={
                    existingQuota ? collapsibleHeaderActionMenuOptions : undefined
                }
                noBodyPadding={true}
                padding={8}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                getRef={props.getRef}
                {...cardShellProps}
            >
                <div className={existingQuota ? undefined : styles['card-content-empty']}>
                    {grid}
                    {totalRow}
                </div>
            </CollapsibleCard>
            {existingQuota && props.existingUsers && isDuplicateModalOpen && (
                <DuplicateToUsersModal
                    isOpen={isDuplicateModalOpen}
                    onClose={() => setIsDuplicateModalOpen(false)}
                    quota={existingQuota}
                    user={user}
                    existingUsers={props.existingUsers}
                    setScrollToUserId={props.setScrollToUserId}
                    onDuplicateStart={props.onDuplicateStart}
                />
            )}
            {existingQuota && isConfirmingDelete && (
                <ConfirmationDialog
                    title='Delete quota'
                    message={`Are you sure you want to delete ${user.name}’s ${props.fiscalYear} quota? This action cannot be undone.`}
                    confirmText='Delete'
                    cancelText='Cancel'
                    isConfirmDestructive={true}
                    onConfirm={() => {
                        setIsDeleting(true);
                        props
                            .onDelete?.()
                            .then(() => {
                                setIsConfirmingDelete(false);
                            })
                            .catch((error) => {
                                setDeleteErrorMessage(
                                    safelyGetGraphQLErrorMessage(error) || undefined
                                );
                            })
                            .finally(() => {
                                setIsDeleting(false);
                            });
                    }}
                    onCancel={() => {
                        setIsConfirmingDelete(false);
                        setDeleteErrorMessage(undefined);
                    }}
                    isSaving={isDeleting}
                    customSaveText={{
                        initial: 'Delete',
                        saving: 'Deleting…',
                        complete: 'Deleted',
                    }}
                    errorMessage={deleteErrorMessage}
                />
            )}
        </>
    );
}
